"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import { useActiveStore } from "@/store/activeStore";
import { DEMO_CTA, DEMO_STORE, type DemoReason } from "@/lib/storeDemo";

interface Store {
  id: number;
  name: string;
  region: string | null;
  type: string | null;
  is_active: boolean;
  created_at: string;
}

interface StoreListOut {
  items: Store[];
}

export default function StoreDashboardPage() {
  const { token, isLoggedIn } = useAuthStore();
  const { activeStoreId, setActiveStoreId } = useActiveStore();
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    axios
      .get<StoreListOut>("/api/stores", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        setStores(r.data.items);
        // 저장된 selection 이 현재 매장 리스트에 없으면 첫 번째로 fallback
        const ids = new Set(r.data.items.map((s) => s.id));
        if (r.data.items.length > 0 && (!activeStoreId || !ids.has(activeStoreId))) {
          setActiveStoreId(r.data.items[0].id);
        }
      })
      .catch(() => setStores([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // 비로그인 / 매장미등록 — 6 기능 쇼케이스 (데모 진입점)
  // Stage 1 (2026-05-30) — 단계적으로 각 페이지에 실제 demo 데이터 채워나감
  if (!isLoggedIn || (!loading && stores.length === 0)) {
    const reason: "login" | "register_store" = !isLoggedIn ? "login" : "register_store";
    return <StorePreviewShowcase reason={reason} />;
  }

  if (loading) {
    return <p className="text-text-sub text-sm py-8 text-center">로딩 중...</p>;
  }

  // 매장 등록됨 — 대시보드 (placeholder, 다음 단계에서 확장)
  const activeStore = stores.find((s) => s.id === activeStoreId);

  return (
    <div className="px-4 py-5">
      <header className="mb-3">
        <h1 className="text-xl font-black text-text">🏪 매장 관리</h1>
      </header>

      {/* 매장 선택·추가·관리 — 한 행 직관 배치 */}
      <div className="mb-4 flex items-center gap-2">
        <span className="text-2xs text-text-sub shrink-0 px-1">선택</span>
        <select
          value={activeStoreId ?? ""}
          onChange={(e) => setActiveStoreId(Number(e.target.value))}
          disabled={stores.length <= 1}
          className={`flex-1 min-w-0 text-sm bg-bg-elevated border rounded-md px-3 py-2 text-text ${
            stores.length > 1
              ? "border-primary/40"
              : "border-border/50 cursor-default opacity-80"
          }`}
          title={stores.length > 1 ? "매장 변경" : "매장 1개 (추가하면 전환 가능)"}
        >
          {stores.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <Link
          href="/store/setup"
          className="text-xs font-semibold px-2.5 py-2 rounded-md bg-primary/10 border border-primary/40 text-primary shrink-0"
          title="매장 추가"
        >
          + 추가
        </Link>
        <Link
          href="/store/manage"
          className="text-xs font-semibold px-2.5 py-2 rounded-md border border-border text-text-sub hover:text-text shrink-0"
          title="매장 정보 수정·삭제·판관비"
        >
          ⚙ 관리
        </Link>
      </div>

      {activeStore && (
        <section className="card mb-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold text-text">{activeStore.name}</h2>
            <span className="text-2xs text-text-sub">
              {activeStore.type ?? "—"} · {activeStore.region ?? "—"}
            </span>
          </div>
          <p className="text-2xs text-text-sub">
            등록일: {activeStore.created_at?.slice(0, 10) ?? "—"}
          </p>
        </section>
      )}

      {/* 사용 순서 — 5개 칩 1줄 (사용자 결정 2026-05-15 — 단순화) */}
      <section className="mb-5">
        <div className="grid grid-cols-3 gap-1.5">
          <StepChip href="/store/stock" emoji="📦" title="입고" />
          <StepChip href="/store/menus" emoji="☕" title="메뉴" />
          <StepChip href="/store/sales" emoji="📝" title="판매" />
          <StepChip href="/store/dashboard" emoji="📊" title="손익" />
          <StepChip href="/store/tax" emoji="📋" title="세금" />
          <StepChip href="/store/staff" emoji="👥" title="직원" />
        </div>
        <p className="text-2xs text-text-sub mt-2 px-1 opacity-70">
          좌 → 우 순서로 진행. 한 번 셋팅 후 매일 5분.
        </p>
      </section>

      <StoreGuide />
    </div>
  );
}

function StepChip({ href, emoji, title }: { href: string; emoji: string; title: string }) {
  return (
    <Link
      href={href}
      className="card flex flex-col items-center justify-center gap-1 py-3 hover:border-primary/40 active:scale-[0.96] transition-all"
    >
      <span className="text-2xl leading-none">{emoji}</span>
      <span className="text-xs font-bold text-text">{title}</span>
    </Link>
  );
}


function StoreGuide() {
  const [open, setOpen] = useState(true);
  return (
    <section className="mt-6 mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left card p-3 hover:border-primary/40 transition-colors"
      >
        <span className="text-sm font-bold text-text">처음 쓰는 사장님을 위한 가이드</span>
        <span className="text-text-sub text-xs">{open ? "▲ 접기" : "▼ 펼치기"}</span>
      </button>

      {open && (
        <div className="mt-3 card p-4 space-y-2.5">
          {[
            { no: 1, title: "입고", desc: "원재료 등록부터 입고·폐기·재고관리까지" },
            { no: 2, title: "메뉴", desc: "매장에서 판매하는 메뉴 등록 (입고 재료와 레시피 연결)" },
            { no: 3, title: "판매", desc: "판매 수량 매출형태별 등록" },
            { no: 4, title: "손익", desc: "매출액 / 원가 / 상품별 마진율 / BEP 관리" },
            { no: 5, title: "세금", desc: "부가세·종합소득세 예측 / 조건에 맞는 감면 찾기" },
            { no: 6, title: "직원", desc: "급여 정확 계산(주휴·4대보험) + 명세서 메일 발송 + 지원금/세제 진단 (두루누리·고용세액공제)" },
          ].map((s) => (
            <div key={s.title} className="flex gap-2.5 items-center">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary text-2xs font-black flex items-center justify-center">
                {s.no}
              </span>
              <p className="flex-1 min-w-0 text-2xs leading-relaxed">
                <span className="font-bold text-text">{s.title}</span>
                <span className="text-text-sub"> · {s.desc}</span>
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function GuideStep({
  no,
  emoji,
  title,
  href,
  detail,
}: {
  no: number;
  emoji: string;
  title: string;
  href: string;
  detail: React.ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-7 h-7 rounded-full bg-primary/20 text-primary text-xs font-black flex items-center justify-center">
        {no}
      </div>
      <div className="flex-1 min-w-0">
        <Link href={href} className="text-sm font-bold text-text hover:text-primary">
          {emoji} {title} <span className="text-2xs text-text-sub font-normal">→</span>
        </Link>
        <div className="text-2xs text-text-sub mt-1 leading-relaxed">{detail}</div>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// 비로그인·매장미등록 사용자용 6 기능 쇼케이스 (Stage 1, 2026-05-30)
// 각 카드는 해당 데모 페이지로 진입. Stage 2~7 가 차례로 데모 데이터 채우면 자연스레 활성화.
// ────────────────────────────────────────────────────────────────────

interface FeatureCard {
  href: string;
  emoji: string;
  title: string;
  hook: string;          // 한 줄 가치 제안
  preview: string;       // 카드 안 가짜 수치 미리보기 (블러 처리)
  ready: boolean;        // 해당 페이지 데모 준비됨? (Stage 2~7 에서 true 로 토글)
}

const FEATURES: FeatureCard[] = [
  { href: "/store/stock",     emoji: "📦", title: "입고",  hook: "원재료 입·출고 + 자동 재고",     preview: "재료 8종 · 유통기한 임박 3건",       ready: true },
  { href: "/store/menus",     emoji: "☕", title: "메뉴",  hook: "메뉴 ROI · 마진율 자동 계산",      preview: "메뉴 12개 · 마진 1위 아메리카노 88%", ready: true },
  { href: "/store/sales",     emoji: "📝", title: "판매",  hook: "수량·결제수단 별 일자 매출 기록",   preview: "오늘 매출 1,047,000원 · 87잔",     ready: true },
  { href: "/store/dashboard", emoji: "📊", title: "손익",  hook: "매출·원가·판관비 → 영업이익·BEP",   preview: "이번 달 영업이익 +9,120,000원",    ready: true },
  { href: "/store/tax",       emoji: "📋", title: "세금",  hook: "부가세·종소세 예상 + 절세 시뮬",     preview: "1Q 부가세 553만 · 절세 2,626만",   ready: true },
  { href: "/store/staff",     emoji: "👥", title: "직원",  hook: "급여·4대보험·지원금 진단",         preview: "두루누리 월 365,000원 예상",       ready: true },
];

function StorePreviewShowcase({ reason }: { reason: DemoReason }) {
  const cta = DEMO_CTA[reason];
  return (
    <div className="px-4 py-5 pb-32">
      <header className="mb-3">
        <h1 className="text-xl font-black text-text">🏪 매장 관리</h1>
        <p className="text-2xs text-text-sub mt-1">{DEMO_STORE.name} — 6개 기능 미리보기</p>
      </header>

      {/* 영업 사례 hero */}
      <div className="card p-3 mb-4 bg-gradient-to-br from-primary/10 to-amber-500/10 border-primary/30">
        <p className="text-2xs text-text-sub">{DEMO_STORE.region} · 직원 {DEMO_STORE.employee_count}명 · 메뉴 {DEMO_STORE.menu_count}개</p>
        <p className="text-sm font-black text-text mt-1">
          월 매출 {(DEMO_STORE.monthly_revenue / 10000).toLocaleString()}만원 카페가 쓰면 이렇게 보입니다
        </p>
        <p className="text-2xs text-text-sub mt-1.5 leading-relaxed">
          매출·원가·재고·세금·직원까지 한 화면에서. 데모로 화면 둘러보고 마음에 들면 시작하세요.
        </p>
      </div>

      {/* 6 기능 카드 그리드 */}
      <div className="grid grid-cols-2 gap-2 mb-5">
        {FEATURES.map((f) => (
          <Link
            key={f.href}
            href={f.href}
            className="card p-3 hover:border-primary/40 active:scale-[0.98] transition-all flex flex-col gap-1.5 relative"
          >
            <div className="flex items-center gap-1.5">
              <span className="text-xl">{f.emoji}</span>
              <span className="text-sm font-bold text-text">{f.title}</span>
              {f.ready
                ? <span className="text-[10px] text-primary font-bold ml-auto">▶ 데모</span>
                : <span className="text-[10px] text-amber-400 ml-auto">곧 공개</span>}
            </div>
            <p className="text-2xs text-text-sub leading-snug">{f.hook}</p>
            <p className="text-[11px] text-text font-semibold mt-auto pt-1.5 border-t border-border/50 filter blur-[1.5px] select-none">
              {f.preview}
            </p>
          </Link>
        ))}
      </div>

      {/* 하단 CTA */}
      <div className="card p-4 border-2 border-primary/60 bg-bg">
        <p className="text-sm font-black text-text">{cta.title}</p>
        <p className="text-2xs text-text-sub leading-relaxed mt-1 mb-3">{cta.body}</p>
        <Link
          href={cta.ctaHref}
          className="block w-full btn-primary text-center text-sm py-2.5 rounded-lg font-bold"
        >
          {cta.ctaText} →
        </Link>
        {reason === "register_store" && (
          <p className="text-2xs text-primary mt-2 text-center">
            💡 음료·디저트 표준 메뉴가 미리 깔려 있어요
          </p>
        )}
      </div>
    </div>
  );
}
